import { createPortal } from 'react-dom';
import { useTranslate } from '@/hooks/useTranslate';
import { Loader } from './Loader';

/**
 * "Finish paying in the window we just opened" overlay. Shared by every flow
 * that hands the payment to another browser window — the PSP redirect tab
 * (<RedirectPayment/>) and the iOS <= 16 Apple Pay fallback tab — so the
 * frozen checkout always explains itself the same way.
 *
 * Rendered via a portal to `document.body`, for the same reason <SuccessScreen/>
 * is: the Modal's content box sets a `transform`, which makes it the containing
 * block AND the stacking context for any `fixed` descendant. Rendered in place,
 * this overlay only covers the modal box and its `z-50` is trapped beneath the
 * Modal's own `z-50` — so sibling overlays paint over it and swallow clicks on
 * the cancel button. The portal anchors it to the viewport and lifts it above
 * the in-modal overlays, while staying under <SuccessScreen/>'s `z-200`.
 */
export const PaymentProcessOverlay = ({ onCancel }: { onCancel?: () => void }) => {
  const { t } = useTranslate();

  return createPortal(
    <div
      data-testid="payment-process-overlay"
      className="fixed inset-0 bg-base-black/50 flex items-center justify-center z-150"
    >
      <div className="bg-white rounded-[var(--radius-xl)] p-8 shadow-xl max-w-sm">
        <div className="flex flex-col items-center gap-4">
          <Loader size="lg" variant="checkout" />
          <p className="text-lg font-medium text-card-foreground">{t('processing')}</p>
          <p className="text-sm text-muted-foreground text-center">{t('pleaseCompletePaymentInWindow')}</p>
          {onCancel && (
            <button
              type="button"
              data-testid="redirect-payment-cancel-button"
              onClick={onCancel}
              className="w-full h-11 px-4 rounded-lg border-1 border-base-border bg-white text-sm font-medium text-base-black cursor-pointer"
            >
              {t('cancelPayment')}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};
